import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatCurrency, formatPct, shortDate } from '../lib/format';
import { convertedRows } from '../lib/currency';
import TableSearch from './TableSearch';

const Money = ({ value, mode }) => (
  <span className="font-mono tabular-nums">{formatCurrency(value, 'INR', { mode })}</span>
);

// Generic 2-level hierarchical dashboard table.
// Parent rows grouped via `parentFn`, child rows grouped via `childFn`.
// Each snapshot column group = Cost, Mkt Val, P&L %  (+ optional % Cost, % MV when showPct).
export default function HierarchicalAssetTable({
  snapshots,
  holdings,
  currency,
  fxRates = {},
  showPct = false,
  moneyMode = 'compact',
  parentFn = (r) => r.mainAssetClass || '—',
  childFn = (r) => r.subAssetClass || '—',
  parentLabel = 'Asset Class',
  searchPlaceholder = 'Search main or sub-asset class…',
  testIdPrefix = 'asset-class-hierarchy',
}) {
  const [query, setQuery] = useState('');

  // Build per-snapshot aggregates: Map<parent, { cost, mv, subs: Map<child, { cost, mv }> }>
  const perSnap = useMemo(() => {
    return snapshots.map((s) => {
      const enriched = convertedRows(holdings[s.id], fxRates, s.id);
      const byParent = new Map();
      let grandCost = 0, grandMv = 0;
      enriched.forEach(({ row, cost, mv }) => {
        const p = parentFn(row);
        const c = childFn(row);
        if (!byParent.has(p)) byParent.set(p, { cost: 0, mv: 0, subs: new Map() });
        const pv = byParent.get(p);
        pv.cost += cost; pv.mv += mv;
        grandCost += cost; grandMv += mv;
        if (!pv.subs.has(c)) pv.subs.set(c, { cost: 0, mv: 0 });
        const sv = pv.subs.get(c);
        sv.cost += cost; sv.mv += mv;
      });
      return { snap: s, byParent, grandCost, grandMv };
    });
  }, [snapshots, holdings, fxRates, parentFn, childFn]);

  // Union of parents + their unioned children across selected snapshots
  const hierarchy = useMemo(() => {
    const main = new Map(); // parent -> Set<child>
    perSnap.forEach(({ byParent }) => {
      byParent.forEach((v, k) => {
        if (!main.has(k)) main.set(k, new Set());
        v.subs.forEach((_sv, sub) => main.get(k).add(sub));
      });
    });
    return Array.from(main.entries())
      .map(([m, subs]) => ({ main: m, subs: Array.from(subs).sort() }))
      .sort((a, b) => a.main.localeCompare(b.main));
  }, [perSnap]);

  // Expanded parent keys — default: all expanded whenever the hierarchy shape changes
  const [expanded, setExpanded] = useState(() => new Set());
  React.useEffect(() => {
    setExpanded(new Set(hierarchy.map((h) => h.main)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchy.length, testIdPrefix]);

  const toggle = (k) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const q = query.trim().toLowerCase();
  const visibleHierarchy = hierarchy
    .map((h) => ({
      ...h,
      subs: q ? h.subs.filter((s) => s.toLowerCase().includes(q)) : h.subs,
      matchMain: !q || h.main.toLowerCase().includes(q),
    }))
    .filter((h) => h.matchMain || h.subs.length > 0);

  const expandAll = () => setExpanded(new Set(hierarchy.map((h) => h.main)));
  const collapseAll = () => setExpanded(new Set());

  const colCount = 1 + snapshots.length * (showPct ? 5 : 3);
  const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <div className="flex-1">
          <TableSearch
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
            testId={`search-${testIdPrefix}`}
            stats={query ? `${visibleHierarchy.length} / ${hierarchy.length}` : null}
          />
        </div>
        <div className="flex gap-1.5 pb-2">
          <button className="btn-secondary !px-2.5 !py-1 text-[11px]" onClick={expandAll} data-testid={`${testIdPrefix}-expand-all`}>
            Expand all
          </button>
          <button className="btn-secondary !px-2.5 !py-1 text-[11px]" onClick={collapseAll} data-testid={`${testIdPrefix}-collapse-all`}>
            Collapse all
          </button>
        </div>
      </div>
      <div className="overflow-x-auto border border-border bg-white">
        <table className="grid-table" data-testid={`${testIdPrefix}-table`}>
          <thead>
            <tr>
              <th rowSpan={2} className="min-w-[240px]">{parentLabel}</th>
              {snapshots.map((s) => (
                <th key={s.id} colSpan={showPct ? 5 : 3} className="text-center">
                  {s.label}
                  <div className="text-[10px] font-normal text-[#787872] font-mono normal-case tracking-normal mt-1">{shortDate(s.date)}</div>
                </th>
              ))}
            </tr>
            <tr>
              {snapshots.map((s) => (
                <React.Fragment key={s.id}>
                  <th className="num">Cost</th>
                  {showPct && <th className="num">% Cost</th>}
                  <th className="num">Mkt Val</th>
                  {showPct && <th className="num">% MV</th>}
                  <th className="num">P&amp;L %</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleHierarchy.length === 0 && (
              <tr><td colSpan={colCount} className="text-center py-6 text-[#AAA] text-xs">No data for current filters.</td></tr>
            )}
            {visibleHierarchy.map(({ main, subs }) => {
              const isOpen = expanded.has(main);
              return (
                <React.Fragment key={main}>
                  <tr style={{ background: '#F6F6F4', cursor: 'pointer' }} onClick={() => toggle(main)} data-testid={`${testIdPrefix}-parent-${main}`}>
                    <td className="font-heading font-bold">
                      <span className="inline-flex items-center gap-1.5">
                        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        {main}
                        <span className="text-[10px] text-[#787872] font-mono font-normal ml-1">({subs.length})</span>
                      </span>
                    </td>
                    {perSnap.map(({ snap, byParent, grandCost, grandMv }) => {
                      const v = byParent.get(main) || { cost: 0, mv: 0 };
                      const pnl = v.cost > 0 ? ((v.mv - v.cost) / v.cost) * 100 : 0;
                      return (
                        <React.Fragment key={snap.id}>
                          <td className="num font-semibold"><Money value={v.cost} mode={moneyMode} /></td>
                          {showPct && <td className="num text-[#575757]">{formatPct(pct(v.cost, grandCost))}</td>}
                          <td className="num font-semibold"><Money value={v.mv} mode={moneyMode} /></td>
                          {showPct && <td className="num text-[#575757]">{formatPct(pct(v.mv, grandMv))}</td>}
                          <td className={`num font-semibold ${pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{formatPct(pnl)}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  {isOpen && subs.map((sub) => (
                    <tr key={`${main}::${sub}`} data-testid={`${testIdPrefix}-child-${main}-${sub}`}>
                      <td className="pl-10 text-[#575757]">{sub}</td>
                      {perSnap.map(({ snap, byParent, grandCost, grandMv }) => {
                        const sv = byParent.get(main)?.subs.get(sub) || { cost: 0, mv: 0 };
                        const pnl = sv.cost > 0 ? ((sv.mv - sv.cost) / sv.cost) * 100 : 0;
                        return (
                          <React.Fragment key={snap.id}>
                            <td className="num"><Money value={sv.cost} mode={moneyMode} /></td>
                            {showPct && <td className="num text-[#575757]">{formatPct(pct(sv.cost, grandCost))}</td>}
                            <td className="num"><Money value={sv.mv} mode={moneyMode} /></td>
                            {showPct && <td className="num text-[#575757]">{formatPct(pct(sv.mv, grandMv))}</td>}
                            <td className={`num ${pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{formatPct(pnl)}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr style={{ background: '#EEEEE8' }}>
              <td className="font-heading font-bold">TOTAL</td>
              {perSnap.map(({ snap, grandCost, grandMv }) => {
                const pnl = grandCost > 0 ? ((grandMv - grandCost) / grandCost) * 100 : 0;
                return (
                  <React.Fragment key={snap.id}>
                    <td className="num font-semibold"><Money value={grandCost} mode={moneyMode} /></td>
                    {showPct && <td className="num font-semibold">100%</td>}
                    <td className="num font-semibold"><Money value={grandMv} mode={moneyMode} /></td>
                    {showPct && <td className="num font-semibold">100%</td>}
                    <td className={`num font-semibold ${pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{formatPct(pnl)}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
